/**
 * Shared drag behavior hook
 *
 * Provides core drag state machine (mousedown → mousemove → mouseup)
 * with cleanup, optional RAF throttling, and enable/disable support.
 *
 * Uses refs for callback and blockId to keep event listener identity stable
 * across re-renders. Without this, parent state changes during drag (e.g.
 * player position updates) would recreate the callback, trigger the cleanup
 * effect, and remove the global mousemove/mouseup listeners mid-drag.
 */

import { useRef, useEffect, useCallback, useState, type MouseEvent } from 'react'

export interface UseDragOptions {
	blockId: string
	onMove: (blockId: string, delta: { deltaX: number; deltaY: number }) => void
	enabled?: boolean
	throttle?: boolean
}

export interface UseDragReturn {
	isDragging: boolean
	handlers: {
		onMouseDown: (e: MouseEvent) => void
	}
}

/**
 * Hook for managing drag state and behavior
 *
 * @example
 * ```tsx
 * const { isDragging, handlers } = useDrag({
 *   blockId: block.id,
 *   onMove: handleMove,
 *   enabled: true,
 *   throttle: true
 * })
 *
 * <div onMouseDown={handlers.onMouseDown}>...</div>
 * ```
 */
export function useDrag({ blockId, onMove, enabled = true, throttle = false }: UseDragOptions): UseDragReturn {
	const [isDragging, setIsDragging] = useState(false)
	const isDraggingRef = useRef(false)
	const dragStartRef = useRef<{ mouseX: number; mouseY: number } | null>(null)
	const dragAnimationFrameRef = useRef<number | null>(null)
	const pendingDragUpdateRef = useRef<{ deltaX: number; deltaY: number } | null>(null)

	// Store latest callback/blockId in refs so event listeners always use
	// current values without needing to be recreated (which would break drag)
	const onMoveRef = useRef(onMove)
	useEffect(() => { onMoveRef.current = onMove }, [onMove])

	const blockIdRef = useRef(blockId)
	useEffect(() => { blockIdRef.current = blockId }, [blockId])

	// Update ref when state changes to avoid stale closures
	useEffect(() => {
		isDraggingRef.current = isDragging
	}, [isDragging])

	// Stable identity — reads from refs, only depends on `throttle` (constant during drag)
	const handleMouseMove = useCallback(
		(e: globalThis.MouseEvent) => {
			if (!isDraggingRef.current || !dragStartRef.current) return

			const deltaX = e.clientX - dragStartRef.current.mouseX
			const deltaY = e.clientY - dragStartRef.current.mouseY

			if (throttle) {
				// Store pending update for RAF throttling
				pendingDragUpdateRef.current = { deltaX, deltaY }

				// Use RAF to throttle updates
				if (!dragAnimationFrameRef.current) {
					dragAnimationFrameRef.current = requestAnimationFrame(() => {
						dragAnimationFrameRef.current = null
						const update = pendingDragUpdateRef.current
						if (update) {
							onMoveRef.current(blockIdRef.current, update)
							pendingDragUpdateRef.current = null
						}
					})
				}
			} else {
				// Direct update without throttling
				onMoveRef.current(blockIdRef.current, { deltaX, deltaY })
			}
		},
		[throttle]
	)

	const handleMouseUp = useCallback(() => {
		setIsDragging(false)

		// Remove global listeners
		document.removeEventListener('mousemove', handleMouseMove)
		document.removeEventListener('mouseup', handleMouseUp)
	}, [handleMouseMove])

	// Cleanup RAF and listeners on unmount
	useEffect(() => {
		return () => {
			if (dragAnimationFrameRef.current) {
				cancelAnimationFrame(dragAnimationFrameRef.current)
			}
			document.removeEventListener('mousemove', handleMouseMove)
			document.removeEventListener('mouseup', handleMouseUp)
		}
	}, [handleMouseMove, handleMouseUp])

	const handleMouseDown = useCallback(
		(e: MouseEvent) => {
			if (!enabled) return

			e.preventDefault()
			e.stopPropagation()

			setIsDragging(true)

			// Store initial mouse position
			dragStartRef.current = {
				mouseX: e.clientX,
				mouseY: e.clientY,
			}

			// Add global listeners
			document.addEventListener('mousemove', handleMouseMove)
			document.addEventListener('mouseup', handleMouseUp)
		},
		[enabled, handleMouseMove, handleMouseUp]
	)

	return {
		isDragging,
		handlers: {
			onMouseDown: handleMouseDown,
		},
	}
}
